import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Botao, Campo, CampoDeSelecao, Modal } from '../../componentes/ui';
import { criarQuadro, type QuadroResumo } from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';

/** Valor do select para as etapas padrão — os demais são ids de quadro. */
const PADRAO = '';

/**
 * "+ Quadro": um quadro novo do pipeline — "Operacional", "Pós-venda"…
 *
 * Nasce com etapas, para já receber empresas: as padrão ("A fazer", "Em
 * andamento", "Concluído") ou as de um quadro que já existe. Depois elas se
 * renomeiam, reordenam e excluem no próprio quadro, como em qualquer outro.
 */
export function NovoQuadro(props: {
  readonly aberto: boolean;
  readonly quadros: readonly QuadroResumo[];
  readonly aoFechar: () => void;
  readonly aoCriar: (quadro: QuadroResumo) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [nome, setNome] = useState('');
  const [etapasDe, setEtapasDe] = useState(PADRAO);

  const criar = useMutation({
    mutationFn: () =>
      criarQuadro({
        nome: nome.trim(),
        ...(etapasDe === PADRAO ? {} : { copiarEtapasDe: etapasDe }),
      }),
    onSuccess: (quadro) => {
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
      props.aoCriar(quadro);
    },
  });

  // Cada abertura começa limpa.
  const { aberto } = props;
  const { reset } = criar;
  useEffect(() => {
    if (!aberto) return;
    setNome('');
    setEtapasDe(PADRAO);
    reset();
  }, [aberto, reset]);

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="Novo quadro"
      descricao="Um quadro é um processo com as suas etapas — o Comercial, o Operacional. A mesma empresa pode estar em mais de um quadro."
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="form-novo-quadro"
            disabled={nome.trim() === ''}
            carregando={criar.isPending}
          >
            Criar quadro
          </Botao>
        </>
      }
    >
      <form
        id="form-novo-quadro"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (nome.trim() !== '') criar.mutate();
        }}
      >
        <Campo
          rotulo="Nome do quadro"
          required
          maxLength={60}
          placeholder="Ex.: Operacional, Pós-venda"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <CampoDeSelecao
          rotulo="Etapas iniciais"
          ajuda="Dá para renomear, reordenar e excluir depois, no próprio quadro."
          value={etapasDe}
          onChange={(e) => setEtapasDe(e.target.value)}
          opcoes={[
            { valor: PADRAO, rotulo: 'Padrão: A fazer, Em andamento, Concluído' },
            ...props.quadros.map((q) => ({ valor: q.id, rotulo: `As mesmas do quadro ${q.nome}` })),
          ]}
        />
        {criar.isError && <ErroDaApi titulo="Não foi possível criar o quadro" erro={criar.error} />}
      </form>
    </Modal>
  );
}
